import React from 'react';

import {
  List,
  Datagrid,
  TextField,
  DateField,
  usePermissions,
  SelectField,
  FunctionField,
  TopToolbar,
  useDataProvider,
  useGetList,
  useNotify,
  useRefresh,
} from 'react-admin';

import {
  Box, Button, Card, Chip, CircularProgress, IconButton, Stack, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import actions, { Actions } from '../../constants/actions';

import hasAccess from '../../utilities/hasAccess';
import useFormStudioEnabled from '../../hooks/useFormStudioEnabled';

import Pagination from '../layout/Pagination';
import NoResults from '../layout/NoResults';
import LinkField from '../layout/LinkField';
import { languagesChoices } from '../../constants/languages';
import resourceNames from '../../constants/resourceNames';
import formStates, { FormStates } from '../../constants/formStates';
import FormStudioColumnLabel from './FormStudioColumnLabel';
import { ChatSessionDetail } from '../pingu/types';
import errorNotification from '../../utilities/errorNotification';
import { FormStateChip } from './FormStatusChips';

const AI_WORKING_REFETCH_INTERVAL_MS = 5000;

type FormListRecord = {
  id: string;
  state?: FormStates;
  inactive?: boolean;
  archived?: boolean;
};

const FormListActions = ({ archived, onCreateDraft, canCreateDraft }: {
  archived: boolean;
  onCreateDraft?: () => void;
  canCreateDraft: boolean;
}) => {
  const navigate = useNavigate();

  if (archived) {
    return (
      <TopToolbar>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/${resourceNames.FORMS}`)}>
          Back to forms
        </Button>
      </TopToolbar>
    );
  }

  return (
    <TopToolbar>
      <Button
        startIcon={<Inventory2OutlinedIcon />}
        onClick={() => navigate(`/${resourceNames.FORMS}/archived`)}
      >
        Archived forms
      </Button>
      {onCreateDraft && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateDraft} disabled={!canCreateDraft}>
          Create form
        </Button>
      )}
    </TopToolbar>
  );
};

// The hidden chip makes the Draft chip and its spinner together as wide as a Published chip.
const AiWorkingDraftChip = () => (
  <Box sx={{ display: 'inline-grid', verticalAlign: 'middle' }}>
    <Chip
      size="small"
      variant="outlined"
      label="Published"
      aria-hidden
      sx={{ gridArea: '1 / 1', visibility: 'hidden' }}
    />
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={0.5}
      sx={{ gridArea: '1 / 1' }}
    >
      <Chip size="small" variant="outlined" color="warning" label="Draft" />
      <Tooltip title="AI is working on this draft">
        <CircularProgress size={14} color="warning" aria-label="AI is working on this draft" />
      </Tooltip>
    </Stack>
  </Box>
);

const FormList = ({ archived = false }: { archived?: boolean }) => {
  const studioEnabled = useFormStudioEnabled();
  const { permissions } = usePermissions<Actions[]>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const canCreate = hasAccess(permissions, actions.FORM_CREATE)
    && hasAccess(permissions, actions.FORM_UPDATE);
  const canCreateFromPublished = canCreate && hasAccess(permissions, actions.FORM_GET);
  const canUpdate = hasAccess(permissions, actions.FORM_UPDATE);
  const { data: processingSessions } = useGetList<ChatSessionDetail>(
    resourceNames.PINGU_SESSIONS,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'createdAt', order: 'DESC' },
      filter: { sessionType: 'formstudio', processing: true },
    },
    {
      enabled: studioEnabled && hasAccess(permissions, actions.PINGU_CHAT_USE),
      refetchInterval: AI_WORKING_REFETCH_INTERVAL_MS,
    },
  );
  const aiWorkingFormIds = new Set(
    processingSessions?.map((session) => session.sessionTypeArgs?.formId),
  );

  const createDraft = async (sourceFormId?: string): Promise<void> => {
    try {
      const resource = sourceFormId
        ? `${resourceNames.FORMS}/${sourceFormId}/drafts`
        : `${resourceNames.FORMS}/drafts`;
      const { data } = await dataProvider.create<FormListRecord>(resource, { data: {} });
      refresh();
      navigate(`/${resourceNames.FORMS}/${data.id}/studio`);
    } catch {
      notify('The form draft could not be created.', { type: 'error' });
    }
  };

  const setArchived = async (record: FormListRecord, nextArchived: boolean): Promise<void> => {
    try {
      await dataProvider.update(`${resourceNames.FORMS}/${record.id}`, {
        id: 'status',
        data: { archived: nextArchived },
        previousData: record,
      });
      notify(nextArchived ? 'Form archived.' : 'Form moved back to the forms list.', { type: 'success' });
      refresh();
    } catch (error) {
      notify(errorNotification(error), { type: 'error' });
    }
  };

  return (
    <List
      resource={resourceNames.FORMS}
      storeKey={archived ? `${resourceNames.FORMS}.archived` : undefined}
      title={archived ? 'Archived forms' : undefined}
      empty={false}
      filter={{
        ...(studioEnabled ? { includeDrafts: true } : {}),
        includeInactive: true,
        ...(archived ? { archived: true } : {}),
      }}
      actions={(
        <FormListActions
          archived={archived}
          onCreateDraft={studioEnabled ? () => createDraft() : undefined}
          canCreateDraft={canCreate}
        />
      )}
      pagination={<Pagination disableEmptyText />}
      exporter={false}
      perPage={50}
    >
      <Card sx={{ margin: 0 }} variant="outlined">
        <Datagrid sx={boldDataGridStyle} bulkActionButtons={false} empty={<NoResults variant="h6" />}>
          {hasAccess(permissions, actions.FORM_GET) && <LinkField sortable={false} source="id" label="ID" />}
          {!hasAccess(permissions, actions.FORM_GET) && <TextField sortable={false} source="id" label="ID" />}
          <TextField sortable={false} source="title" />
          <FunctionField<FormListRecord>
            label="Status"
            render={(record) => (
              record.state === formStates.DRAFT && aiWorkingFormIds.has(record.id)
                ? <AiWorkingDraftChip />
                : <FormStateChip state={record.state} />
            )}
          />
          {studioEnabled && (
            <FunctionField<FormListRecord>
              label={<FormStudioColumnLabel />}
              sortable={false}
              render={(record) => (
                <Stack direction="row" spacing={1}>
                  {record.state === formStates.DRAFT && canUpdate ? (
                    <Tooltip title={record.inactive ? 'Inactive forms cannot be edited in Form Studio' : ''}>
                      <span>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          disabled={record.inactive}
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/${resourceNames.FORMS}/${record.id}/studio`);
                          }}
                        >
                          Open Studio
                        </Button>
                      </span>
                    </Tooltip>
                  ) : null}
                  {record.state !== formStates.DRAFT && !record.inactive
                    && canCreateFromPublished && (
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={(event) => {
                        event.stopPropagation();
                        createDraft(record.id);
                      }}
                    >
                      Create draft
                    </Button>
                  )}
                </Stack>
              )}
            />
          )}
          <SelectField sortable={false} source="language" choices={languagesChoices} />
          <DateField sortable source="createdAt" showTime />
          <DateField sortable source="updatedAt" label="Updated at" showTime />
          {canUpdate && (
            <FunctionField<FormListRecord>
              label=""
              textAlign="right"
              render={(record) => {
                if (archived) {
                  return (
                    <Tooltip title="Move back to the forms list">
                      <IconButton
                        size="small"
                        aria-label="Unarchive"
                        onClick={(event) => {
                          event.stopPropagation();
                          setArchived(record, false);
                        }}
                      >
                        <UnarchiveOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip title={record.inactive ? 'Archive' : 'Only inactive forms can be archived'}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Archive"
                        disabled={!record.inactive}
                        onClick={(event) => {
                          event.stopPropagation();
                          setArchived(record, true);
                        }}
                      >
                        <ArchiveOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              }}
            />
          )}
        </Datagrid>
      </Card>
    </List>
  );
};

export default FormList;
